import React, { useState } from 'react';
import { Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import Modal, { ModalProps } from 'shared/lib/components/modals/modal';
import { useTranslation } from 'react-i18next';
import { emptySelectItemId } from 'shared/lib/utils/globalUtils';
import PlacementSelection from 'widgets/selectPlacement/placementSelection';
import { InitialPlacement, PlacementParams } from 'entities/types';

export default observer(function RestoreModal({
  initialFolderId,
  initialSectionId,
  onSubmit,
  applyText,
  headerText,
  handleClose,
  isOpen,
  isFolderRequired = false,
}: InitialPlacement &
  ModalProps & {
    onSubmit: (params: PlacementParams) => Promise<any>;
    applyText?: string;
    headerText?: string;
    isFolderRequired?: boolean;
  }) {
  const { t } = useTranslation();
  applyText = applyText || t('common.save');
  headerText = headerText || t('common.restoring');

  const [isLoading, setIsLoading] = useState(false);

  const [selectedFolderId, setSelectedFolderId] = useState<string>(
    initialFolderId || ''
  );
  const [selectedSectionId, setSelectedSectionId] = useState<string>(
    initialSectionId || ''
  );

  const isDisabled =
    !selectedSectionId ||
    selectedSectionId === emptySelectItemId ||
    (isFolderRequired && selectedFolderId === emptySelectItemId);

  return (
    <Modal isOpen={isOpen} handleClose={handleClose}>
      <Typography variant="f24">{headerText}</Typography>

      <PlacementSelection
        initialSectionId={selectedSectionId}
        initialFolderId={selectedFolderId || ''}
        onChangeFolder={(folderId) => setSelectedFolderId(folderId || '')}
        onChangeSection={(sectionId) => setSelectedSectionId(sectionId)}
        labelProps={{
          textFolder: t('common.selectFolder'),
          textSection: t('common.selectSection'),
          typographyVariantSize: 'f16',
        }}
      />

      <ButtonsContainer>
        <SecondaryButton onClick={handleClose}>
          {t('common.cancel')}
        </SecondaryButton>
        <PrimaryButton
          loading={isLoading}
          disabled={isDisabled}
          onClick={async () => {
            setIsLoading(true);

            const params: PlacementParams = {
              sectionId: selectedSectionId,
            };

            if (selectedFolderId) {
              params.folderId = selectedFolderId;
            }

            try {
              await onSubmit({ ...params });
            } catch (e) {
              console.error(e);
            }

            setIsLoading(false);
          }}
        >
          {applyText}
        </PrimaryButton>
      </ButtonsContainer>
    </Modal>
  );
});
